/**
 * Build a 12-month YearCalendar cell array from 5-year historical soil
 * temperature data. Temperature is bucketed into 4 bands (cold / prime /
 * warm / hot). The data still exposes the "best month" closest to the user's
 * grass-season target temperature, but UI should highlight `currentMonth`
 * unless it explicitly labels the target/optimal month.
 */

import type { YearCalendarCell, HistoricalAvgResponse } from "./year-calendar-types"
import type { GrassCategory } from "./soil-temp-transforms"

const TEMP_COLD = "#bfdbfe" // blue-200 — <50°F, dormant
const TEMP_PRIME = "#16a34a" // green-600 — 50-65°F, pre-em + cool seeding window
const TEMP_WARM = "#86efac" // green-300 — 65-75°F, warm-season prime
const TEMP_HOT = "#fde68a" // amber-200 — >75°F, summer dormancy stress

export type SoilTempBand = "cold" | "prime" | "warm" | "hot"

export interface SoilTempCalendar {
  cells: YearCalendarCell[]
  /** Index 0-11 of the month with the soil temp closest to the target band. */
  bestMonth: number
  /** Index 0-11 of the user's current calendar month. */
  currentMonth: number
  /** Monthly average temps in °F, indexed 0-11. NaN for months with no samples. */
  monthlyAverages: number[]
  bands: Array<{ band: SoilTempBand; swatch: string; label: string; range: string }>
}

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export function bandForTemp(temp: number): SoilTempBand {
  if (temp < 50) return "cold"
  if (temp < 65) return "prime"
  if (temp < 75) return "warm"
  return "hot"
}

function fillForBand(band: SoilTempBand): string {
  switch (band) {
    case "cold":
      return TEMP_COLD
    case "prime":
      return TEMP_PRIME
    case "warm":
      return TEMP_WARM
    case "hot":
      return TEMP_HOT
  }
}

function targetTempForGrass(grass: GrassCategory): number {
  // Mid of the band each grass type wants for active growth / seeding.
  return grass === "warm" ? 70 : 55
}

export function buildSoilTempCalendar(
  fiveYearData: HistoricalAvgResponse | null,
  grass: GrassCategory,
  today: Date = new Date(),
): SoilTempCalendar | null {
  if (!fiveYearData?.data || fiveYearData.data.length === 0) return null

  // Group MM-DD daily averages by month and compute the mean per month.
  const sums = new Array<number>(12).fill(0)
  const counts = new Array<number>(12).fill(0)
  for (const entry of fiveYearData.data) {
    const [mmStr] = entry.date.split("-")
    const monthIndex = parseInt(mmStr, 10) - 1
    if (monthIndex < 0 || monthIndex > 11) continue
    if (!Number.isFinite(entry.avgTemp)) continue
    sums[monthIndex] += entry.avgTemp
    counts[monthIndex] += 1
  }

  const monthlyAverages = sums.map((sum, i) => (counts[i] > 0 ? sum / counts[i] : NaN))
  const hasAnyMonth = monthlyAverages.some(t => Number.isFinite(t))
  if (!hasAnyMonth) return null

  const cells: YearCalendarCell[] = MONTH_LABELS.map((label, i) => {
    const temp = monthlyAverages[i]
    return {
      i,
      label,
      fill: Number.isFinite(temp) ? fillForBand(bandForTemp(temp)) : "#f9fafb",
    }
  })

  const target = targetTempForGrass(grass)
  let bestMonth = 0
  let bestDiff = Infinity
  monthlyAverages.forEach((temp, i) => {
    if (!Number.isFinite(temp)) return
    const diff = Math.abs(temp - target)
    if (diff < bestDiff) {
      bestDiff = diff
      bestMonth = i
    }
  })

  const bands: SoilTempCalendar["bands"] = [
    { band: "cold", swatch: TEMP_COLD, label: "Cold", range: "<50°F" },
    { band: "prime", swatch: TEMP_PRIME, label: "Prime", range: "50–65°F" },
    { band: "warm", swatch: TEMP_WARM, label: "Warm", range: "65–75°F" },
    { band: "hot", swatch: TEMP_HOT, label: "Hot", range: ">75°F" },
  ]

  return { cells, bestMonth, currentMonth: today.getMonth(), monthlyAverages, bands }
}
